import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import CloseIcon from '@mui/icons-material/Close'
import CloseFullscreenOutlinedIcon from '@mui/icons-material/CloseFullscreenOutlined'
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined'
import OrbitReportDraftAssistant from './OrbitReportDraftAssistant'

const PROMPTS = [
  {
    id: 'opportunities',
    eyebrow: 'Sales',
    title: 'Where are my biggest opportunities?',
    prompt: 'What are the biggest opportunities for this client right now?',
    response: 'Indie would summarise this as the top classes with upside, the biggest gross margin swings, and the locations needing immediate review. In phase 2 this card will call the live Orbit workflow.',
  },
  {
    id: 'inventory',
    eyebrow: 'Inventory',
    title: 'What inventory should I react to?',
    prompt: 'Show me inventory risk areas and where to focus attention.',
    response: 'Expect this panel to call out overstock pressure, markdown exposure, and the areas where current sell-through is not supporting receipt plans.',
  },
  {
    id: 'trend',
    eyebrow: 'Trend',
    title: 'Explain the trend behind the latest performance.',
    prompt: 'Explain the biggest performance trend for this client in plain language.',
    response: 'In the wired pass this will pull the underlying context bootstrap, summary, and validated chat execution so the answer stays client-scoped and evidence-based.',
  },
]

function createMessage(role, content) {
  return {
    id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role,
    content,
  }
}

function buildReportWelcomeMessage(client) {
  return `Let’s build a custom report for ${client?.Name || 'this client'}. Start with a freeform instruction, review the draft, and then save it into the existing report artifact flow.`
}

function buildPrepareSummary(requestPayload, response) {
  const instruction = requestPayload?.user_instruction_text || requestPayload?.message
  return {
    user: instruction || 'Prepare a custom Inventory Action report draft.',
    assistant: response?.assistant_summary || 'I prepared the first report draft.',
  }
}

function appendDraftDiffSummary(baseText, response) {
  const diffSummary = response?.draft_diff?.short_summary || response?.draft_diff?.summary
  return diffSummary ? `${baseText} ${diffSummary}` : baseText
}

function buildUpdateSummary(metadata, response) {
  return {
    user: metadata?.changeRequest || 'Update this draft.',
    assistant: appendDraftDiffSummary(response?.assistant_summary || 'I updated the report draft.', response),
  }
}

function buildReviewSummary(metadata, response) {
  const nextAction = response?.assistant_next_action ? ` Next action: ${response.assistant_next_action}.` : ''
  return {
    user: metadata?.reviewRequestText || '',
    assistant: appendDraftDiffSummary(response?.assistant_summary
      ? `${response.assistant_summary}${nextAction}`
      : 'I reviewed the draft and it is ready for your confirmation.', response),
  }
}

function buildSaveSummary(response) {
  if (response?.filter_id) {
    if (response?.saved_action === 'unchanged') {
      return `No material changes were detected for filter #${response.filter_id}. Regeneration is not required.`
    }
    if (response?.saved_action === 'updated') {
      return appendDraftDiffSummary(
        `Updated existing filter #${response.filter_id}. Regenerate the report file to reflect the latest draft changes.`,
        response,
      )
    }
    return `Saved the draft as filter #${response.filter_id}. You can now generate the report file through the existing artifact pipeline.`
  }
  return response?.assistant_summary || 'The custom report draft has been saved.'
}

function buildGenerationSummary(response, metadata) {
  const filterId = response?.filter_id || metadata?.filterId
  const status = response?.report_file?.status_name || response?.report_file?.status || 'started'
  return filterId
    ? `Started report generation for filter #${filterId}. Current status: ${status}.`
    : `Started report generation. Current status: ${status}.`
}

function buildStatusSummary(response, metadata) {
  const filterId = response?.filter_id || metadata?.filterId
  const status = response?.report_file?.status_name || response?.report_file?.status || response?.status || 'unknown'
  return filterId
    ? `Filter #${filterId} status refreshed: ${status}.`
    : `Report artifact status refreshed: ${status}.`
}

function buildArtifactHydratedSummary(artifact) {
  const filterId = artifact?.filter_id
  const filterName = artifact?.filter_name || artifact?.artifact?.filter_name || 'saved report'
  return filterId
    ? `Loaded saved filter #${filterId} (${filterName}) into the assistant.`
    : `Loaded ${filterName} into the assistant.`
}

function IndieChatPanel({ mode = 'flyout', client, onClose, onToggleDock, reportAssistant = null, embedded = false }) {
  const [messages, setMessages] = useState([])
  const [draft, setDraft] = useState('')
  const [activePromptId, setActivePromptId] = useState('')
  const scrollRef = useRef(null)
  const embeddedReportAssistantInitialisedRef = useRef(false)
  const isInlineMode = mode === 'docked' || mode === 'page'
  const isStandalonePage = mode === 'page'
  const isReportAssistantMode = Boolean(reportAssistant?.enabled)
  const hasConversation = messages.length > 0

  const clientLabel = useMemo(() => {
    if (!client?.Name && !client?.ClientCode) {
      return 'No client selected'
    }

    return client?.ClientCode ? `${client.Name} (${client.ClientCode})` : client.Name
  }, [client])

  const appendAssistant = (content) => {
    window.setTimeout(() => {
      setMessages((current) => [...current, createMessage('assistant', content)])
    }, 280)
  }

  const appendConversationTurn = (userContent, assistantContent) => {
    setMessages((current) => {
      const next = [...current]
      if (userContent) {
        next.push(createMessage('user', userContent))
      }
      if (assistantContent) {
        next.push(createMessage('assistant', assistantContent))
      }
      return next
    })
  }

  useEffect(() => {
    if (isReportAssistantMode) {
      setMessages([createMessage('assistant', buildReportWelcomeMessage(client))])
      setDraft('')
      setActivePromptId('')
    } else {
      setMessages([])
    }
  }, [client?.ClientCode, client?.Name, isReportAssistantMode])

  useEffect(() => {
    if (scrollRef.current) {
      if (embedded && isReportAssistantMode && !embeddedReportAssistantInitialisedRef.current) {
        scrollRef.current.scrollTop = 0
        if (messages.length > 0) {
          embeddedReportAssistantInitialisedRef.current = true
        }
        return
      }

      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [embedded, isReportAssistantMode, messages])

  const handleSend = (nextPrompt, promptId = '') => {
    const trimmed = String(nextPrompt || '').trim()
    if (!trimmed) {
      return
    }

    setMessages((current) => [...current, createMessage('user', trimmed)])
    setDraft('')
    setActivePromptId(promptId)

    const cannedResponse = PROMPTS.find((item) => item.id === promptId)?.response
      || 'This is the phase 1 mock reply. In phase 2 this composer will use the validated Orbit / Indie chat request path and stream the answer inline.'

    appendAssistant(cannedResponse)
  }

  const panelRadius = mode === 'flyout' ? 6 : 4

  const reportAssistantBindings = isReportAssistantMode
    ? {
        ...reportAssistant,
        onDraftPrepared: (response, requestPayload) => {
          const summary = buildPrepareSummary(requestPayload, response)
          appendConversationTurn(summary.user, summary.assistant)
          reportAssistant?.onDraftPrepared?.(response, requestPayload)
        },
        onDraftUpdated: (response, metadata) => {
          const summary = buildUpdateSummary(metadata, response)
          appendConversationTurn(summary.user, summary.assistant)
          reportAssistant?.onDraftUpdated?.(response, metadata)
        },
        onDraftReviewed: (response, requestPayload) => {
          const summary = buildReviewSummary(requestPayload, response)
          appendConversationTurn(summary.user, summary.assistant)
          reportAssistant?.onDraftReviewed?.(response, requestPayload)
        },
        onDraftSaved: (response, requestPayload) => {
          appendConversationTurn(null, buildSaveSummary(response))
          reportAssistant?.onDraftSaved?.(response, requestPayload)
        },
        onArtifactGenerationStarted: (response, metadata) => {
          appendConversationTurn(null, buildGenerationSummary(response, metadata))
          reportAssistant?.onArtifactGenerationStarted?.(response, metadata)
        },
        onArtifactStatusRefreshed: (response, metadata) => {
          appendConversationTurn(null, buildStatusSummary(response, metadata))
          reportAssistant?.onArtifactStatusRefreshed?.(response, metadata)
        },
        onArtifactHydrated: (artifact) => {
          appendConversationTurn(null, buildArtifactHydratedSummary(artifact))
          reportAssistant?.onArtifactHydrated?.(artifact)
        },
        onRequestError: (errorPayload) => {
          appendConversationTurn(null, `I hit an issue during ${errorPayload?.phase || 'this step'}: ${errorPayload?.message || 'Please try again.'}`)
          reportAssistant?.onRequestError?.(errorPayload)
        },
      }
    : reportAssistant

  const renderMessageList = () => (
    <Stack spacing={1.5}>
      {messages.map((message) => (
        <Stack
          key={message.id}
          direction="row"
          spacing={1.25}
          sx={{
            justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: '88%',
              px: 1.5,
              py: 1.2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: message.role === 'user' ? 'rgba(77, 54, 96, 0.22)' : 'rgba(77, 54, 96, 0.12)',
              backgroundColor: message.role === 'user' ? '#efe7f8' : 'white',
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', mb: 0.45, fontWeight: 700, color: '#6b5a7f' }}>
              {message.role === 'user' ? 'You' : 'Indie'}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
              {message.content}
            </Typography>
          </Paper>
        </Stack>
      ))}
    </Stack>
  )

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        borderRadius: embedded ? 0 : panelRadius,
        overflow: 'hidden',
        border: embedded ? 'none' : '1px solid rgba(116, 88, 143, 0.22)',
        background: embedded ? 'transparent' : 'linear-gradient(180deg, #fcfbff 0%, #f6f1fb 100%)',
        boxShadow: embedded
          ? 'none'
          : (mode === 'flyout'
            ? '0 22px 60px rgba(40, 22, 60, 0.28)'
            : '0 10px 35px rgba(40, 22, 60, 0.14)'),
      }}
    >
      {!embedded && (
        <Box
          sx={{
            px: 2.25,
            py: 1.75,
            color: 'white',
            background: 'linear-gradient(135deg, #4d3660 0%, #2d1f3f 100%)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
          <Stack spacing={1} sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.18)',
                }}
              >
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Ask Indie
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                  {isReportAssistantMode ? 'Page-aware report assistant' : 'Retail consultant chat surface'}
                </Typography>
              </Box>
            </Stack>
            <Chip
              size="small"
              label={clientLabel}
              sx={{
                alignSelf: 'flex-start',
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.18)',
                '& .MuiChip-label': { px: 1.1 },
              }}
            />
          </Stack>

          {!isStandalonePage && (
            <Stack direction="row" spacing={0.5}>
              <IconButton
                size="small"
                onClick={onToggleDock}
                sx={{ color: 'white' }}
                title={mode === 'flyout' ? 'Embed on page' : 'Return to flyout'}
              >
                {isInlineMode ? <CloseFullscreenOutlinedIcon fontSize="small" /> : <OpenInFullOutlinedIcon fontSize="small" />}
              </IconButton>
              <IconButton size="small" onClick={onClose} sx={{ color: 'white' }} title="Close chat">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Stack>
        </Box>
      )}

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: embedded ? 0 : 2.25,
          py: embedded ? 0 : 2,
          background: embedded
            ? 'transparent'
            : 'linear-gradient(180deg, rgba(248,244,252,0.3) 0%, rgba(255,255,255,0.92) 28%)',
        }}
      >
        <Stack spacing={1.5}>
          {isReportAssistantMode ? (
            <>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 4,
                  border: '1px solid rgba(109, 82, 138, 0.14)',
                  background: 'linear-gradient(135deg, rgba(94, 61, 121, 0.12) 0%, rgba(255,255,255,0.96) 70%)',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      borderRadius: '16px',
                      color: '#4d3660',
                      background: 'linear-gradient(135deg, rgba(94, 61, 121, 0.18) 0%, rgba(135, 99, 177, 0.08) 100%)',
                      border: '1px solid rgba(94, 61, 121, 0.12)',
                    }}
                  >
                    <AutoAwesomeOutlinedIcon />
                  </Box>
                  <Stack spacing={0.75}>
                    <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#2d1f3f' }}>
                      Your report assistant is ready!
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#5f5670', lineHeight: 1.7 }}>
                      {`Use this panel to prepare, revise, review, and save a custom Inventory Action report for ${client?.Name || 'this client'}.`}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label="Client-aware" sx={{ backgroundColor: 'rgba(94, 61, 121, 0.08)' }} />
                      <Chip size="small" label="Report library context" sx={{ backgroundColor: 'rgba(94, 61, 121, 0.08)' }} />
                      <Chip size="small" label="Structured artifact save" sx={{ backgroundColor: 'rgba(94, 61, 121, 0.08)' }} />
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>

              <Box>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b5a7f' }}>
                  Workflow transcript
                </Typography>
                {renderMessageList()}
              </Box>

              <OrbitReportDraftAssistant client={client} reportAssistant={reportAssistantBindings} />
            </>
          ) : !hasConversation && (
            <>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 4,
                  border: '1px solid rgba(109, 82, 138, 0.14)',
                  background: 'linear-gradient(135deg, rgba(94, 61, 121, 0.12) 0%, rgba(255,255,255,0.96) 70%)',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      borderRadius: '16px',
                      color: '#4d3660',
                      background: 'linear-gradient(135deg, rgba(94, 61, 121, 0.18) 0%, rgba(135, 99, 177, 0.08) 100%)',
                      border: '1px solid rgba(94, 61, 121, 0.12)',
                    }}
                  >
                    <AutoAwesomeOutlinedIcon />
                  </Box>
                  <Stack spacing={0.75}>
                    <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#2d1f3f' }}>
                      Your AI Assistant is ready!
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#5f5670', lineHeight: 1.7 }}>
                      {`Ask Indie about opportunities, inventory, performance trends, and next steps for ${client?.Name || 'this client'}.`}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label="Client-aware" sx={{ backgroundColor: 'rgba(94, 61, 121, 0.08)' }} />
                      <Chip size="small" label="Retail context" sx={{ backgroundColor: 'rgba(94, 61, 121, 0.08)' }} />
                      <Chip size="small" label="Prompt guided" sx={{ backgroundColor: 'rgba(94, 61, 121, 0.08)' }} />
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>

              <Box>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b5a7f' }}>
                  Start with a question
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.25, color: '#71677f', lineHeight: 1.6 }}>
                  Choose a starting point below or type your own question in the composer.
                </Typography>
                <Stack spacing={1}>
                  {PROMPTS.map((prompt) => (
                    <Paper
                      key={prompt.id}
                      variant="outlined"
                      onClick={() => handleSend(prompt.prompt, prompt.id)}
                      sx={{
                        p: 1.25,
                        borderRadius: 3,
                        cursor: 'pointer',
                        borderColor: activePromptId === prompt.id ? 'primary.main' : 'rgba(77, 54, 96, 0.12)',
                        backgroundColor: activePromptId === prompt.id ? 'rgba(77, 54, 96, 0.06)' : 'white',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 8px 18px rgba(77, 54, 96, 0.1)',
                        },
                      }}
                    >
                      <Typography variant="caption" sx={{ display: 'block', color: '#7c6b91', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                        {prompt.eyebrow}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.35 }}>
                        {prompt.title}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {!isReportAssistantMode && hasConversation && renderMessageList()}
        </Stack>
      </Box>

      {!isReportAssistantMode && (
        <>
          {!embedded && <Divider />}

          <Box sx={{ p: embedded ? 0 : 2.25, pt: embedded ? 2 : 2.25, backgroundColor: embedded ? 'transparent' : 'rgba(255,255,255,0.9)' }}>
            <Stack spacing={1.25}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={5}
                placeholder="Ask Indie anything about this client…"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
              />
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Phase 1 mock composer — phase 2 will connect this panel to the live Orbit chat workflow.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => handleSend(draft)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 999,
                    px: 2,
                    background: 'linear-gradient(135deg, #5e3d79 0%, #7d58a5 100%)',
                  }}
                >
                  Send
                </Button>
              </Stack>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  )
}

export default IndieChatPanel

